import { useState } from "react";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, Note, Notes, useWidgetText } from "../shared";
import { counterDate, counterSpan, counterTitle, spokenValue } from "../station";
import type { WidgetProps } from "../types";
import { ProLocked } from "./WeatherStation";

const BUTTON =
  "rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-white";

/**
 * reset_counter (weather-station-widgets WSW-10 to WSW-14, WSW-21): a summed value since its counter was last reset,
 * the period before, a Reset button that asks first, and Earlier. Only a viewer with control may reset (WSW-13).
 */
export function ResetCounter(props: WidgetProps) {
  const { widget, input, now, tz, readOnly, onCounterReset, onCounterEarlier, counterNote } = props;
  const fmt = useWidgetText();
  const [asking, setAsking] = useState(false);
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const answer = bound?.binding.datapoint
    ? input.counters?.[key(bound.binding.device, bound.binding.datapoint)]
    : undefined;
  if (answer?.problem?.code === "plan_feature") return <ProLocked props={props} label={(text) => `${name}: ${text}`} />;
  const counter = answer?.counter;
  const icon = <PointIcon point={point} />;
  const resetButton = (enabled: boolean) => (
    <button type="button" className={BUTTON} disabled={!enabled} onClick={() => setAsking(true)}>
      {fmt.t("counter.reset")}
    </button>
  );
  if (!point || !counter || !point.latest) {
    const title = point && counter ? counterTitle(point, counter.since, now, fmt.t, tz) : name;
    return (
      <Frame widget={widget} state="empty" title={title} icon={icon} label={`${title}: ${fmt.t("state.noData")}`}>
        <p className="text-gray-700">{fmt.t("state.noData")}</p>
        {counter && <div className="mt-auto">{resetButton(false)}</div>}
      </Frame>
    );
  }
  if (counter.value === null) {
    const text = fmt.t("counter.counting", { date: counterDate(counter.since, now, fmt.t, tz) });
    return (
      <Frame widget={widget} state="counting" title={name} icon={icon} label={text}>
        <p className="text-gray-700">{text}</p>
        <div className="mt-auto">{resetButton(false)}</div>
      </Frame>
    );
  }
  const title = counterTitle(point, counter.since, now, fmt.t, tz);
  const value = fmt.value(counter.value, point);
  const prev = counter.previous;
  const canReset = counter.can_reset && readOnly !== true;
  const offline = bound.device?.state === "offline";
  const seen = bound.device?.last_seen_at;
  return (
    <Frame
      widget={widget}
      state={offline ? "offline" : "normal"}
      title={title}
      icon={icon}
      label={`${title}: ${spokenValue(counter.value, point, fmt.locale, fmt.t)}`}
    >
      <p className="truncate text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">{value}</p>
      {prev && prev.value !== null && (
        <p className="text-xs text-gray-700">
          {fmt.t("counter.before", {
            value: fmt.value(prev.value, point),
            period: counterSpan(prev.from, prev.to, now, fmt.t, tz),
          })}
        </p>
      )}
      {counterNote && <Note tone="warn">{counterNote}</Note>}
      {asking ? (
        <div role="alertdialog" aria-label={fmt.t("counter.reset")} className="mt-auto space-y-2">
          <p className="text-sm text-gray-900">{fmt.t("counter.confirm", { title, value })}</p>
          <div className="flex gap-2">
            <button
              type="button"
              className={BUTTON}
              onClick={() => {
                setAsking(false);
                onCounterReset?.(counter.since);
              }}
            >
              {fmt.t("counter.reset")}
            </button>
            <button type="button" className={BUTTON} onClick={() => setAsking(false)}>
              {fmt.t("counter.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          {resetButton(canReset)}
          {prev && (
            <button
              type="button"
              onClick={onCounterEarlier}
              className="text-sm font-medium text-brand underline-offset-2 hover:underline"
            >
              {fmt.t("counter.earlier")}
            </button>
          )}
        </div>
      )}
      {!counter.can_reset && <p className="text-xs text-gray-700">{fmt.t("counter.noControl")}</p>}
      {offline && seen && <Notes items={[{ text: fmt.t("statistics.offline", { age: fmt.age(seen, now) }) }]} />}
    </Frame>
  );
}
